import type {
  GetDipBatchChangeRequestsJobsQuery,
  GetDipBatchEvidencesJobsQuery,
  GetDipDetailQuery,
  GetDipRecordComposersQuery,
} from "~/features/dataImportProjects/gqls";
import type {
  DataImportProjectListRow,
  DipDataSourceStatsSummary,
  DipNodeVariant,
  DipPeekTarget,
  DipPeekType,
  DipPipelineColumn,
  DipPipelineItem,
  DipProjectInfoUI,
  DipSchemaGraph,
  DipSchemaPair,
  DipSummaryGroup,
  DipTone,
} from "~/features/dataImportProjects/types";
import { DataImportProjectUtil } from "./dataImportProject";
import {
  makeBatchChangeRequestsJobNode,
  makeBatchEvidencesJobNode,
  makeChangeRequestsNode,
  makeComposerNode,
  makeDataSourceNode,
  makeEvidenceDraftsNode,
  makeEvidenceImporterNode,
  makeEvidenceSetNode,
  makeRecordsNode,
} from "./nodeFactory";
import { peekPathFor } from "./peekPath";
import { statusTone } from "./statusTone";

type DataImportProjectWithReferences = {
  id: string;
  seq: number;
  name: string;
  statusId: string;
  documentUrl?: string | null;
  updatedAt: string;
  deactivatedAt?: string | null;
  dataImportProjectStatusLogs: ReadonlyArray<{ statusId: string; createdAt: string }>;
  dataSourceImportProjects: ReadonlyArray<{
    dataSource: {
      dataSourceReferences: ReadonlyArray<{
        reference: { title: string; shortCode: string };
      }>;
    };
  }>;
};

type DipDetail = NonNullable<GetDipDetailQuery["dataImportProjectsByPk"]>;
type DipBatchChangeRequestsJobs = GetDipBatchChangeRequestsJobsQuery["batchChangeRequestsJobs"];
type DipBatchEvidencesJobs = GetDipBatchEvidencesJobsQuery["batchEvidencesJobs"];
type DipProjectDataSources = DipDetail["dataSourceImportProjects"];

const PLANNING = "Planning";
const DONE = "Done";

function logDate(project: DataImportProjectWithReferences, statusId: string): string | null {
  return project.dataImportProjectStatusLogs.find((log) => log.statusId === statusId)?.createdAt ?? null;
}

export function toDataImportProjectListRows(
  projects: readonly DataImportProjectWithReferences[],
): DataImportProjectListRow[] {
  return projects.map((project) => {
    const references = [
      ...new Map(
        project.dataSourceImportProjects.flatMap((link) =>
          link.dataSource.dataSourceReferences.map((dataSourceReference) => [
            dataSourceReference.reference.shortCode,
            `${dataSourceReference.reference.shortCode}: ${dataSourceReference.reference.title}`,
          ]),
        ),
      ).values(),
    ];

    return {
      id: project.id,
      seq: project.seq,
      seqLabel: DataImportProjectUtil.projectSeqId(project.seq),
      name: project.name,
      statusId: project.statusId,
      documentUrl: project.documentUrl ?? null,
      startAtISO: logDate(project, DataImportProjectUtil.startStatusId()),
      endAtISO: logDate(project, DataImportProjectUtil.completeStatusId()),
      updatedAtISO: project.updatedAt,
      isDeactivated: project.deactivatedAt != null,
      references,
      referencesText: references.join(", "),
    };
  });
}

function dataSourceDetailPath(dataImportProjectId: string, dataSourceId: string): string {
  return peekPathFor("dataSource", dataSourceId, dataImportProjectId);
}

function evidenceSetDetailPath(dataImportProjectId: string, evidenceSetId: string): string {
  return peekPathFor("evidenceSet", evidenceSetId, dataImportProjectId);
}

function batchEvidencesJobDetailPath(dataImportProjectId: string, jobId: string): string {
  return peekPathFor("batchEvidencesJob", jobId, dataImportProjectId);
}

function batchChangeRequestsJobChangeRequestsPath(dataImportProjectId: string, jobId: string): string {
  return peekPathFor("batchChangeRequestsJob", jobId, dataImportProjectId, "changeRequests");
}

function batchChangeRequestsJobInfoPath(dataImportProjectId: string, jobId: string): string {
  return peekPathFor("batchChangeRequestsJob", jobId, dataImportProjectId, "info");
}

function composerPeekType(variant: DipNodeVariant): DipPeekType {
  return variant === "entity"
    ? "entityComposer"
    : variant === "record_update"
      ? "recordUpdateComposer"
      : "relationComposer";
}

function composerDetailPath(composerId: string, variant: DipNodeVariant, dataImportProjectId: string): string {
  return peekPathFor(composerPeekType(variant), composerId, dataImportProjectId);
}

// A composer is entity/relation when its matching subtype marker is present; otherwise a
// present record_update marker identifies a record_update composer. An untyped composer
// (no subtype marker at all) keeps the pre-existing relation fallback so nothing regresses.
function composerNodeVariant(composer: {
  entityComposerDefinition?: unknown | null;
  relationComposerDefinition?: unknown | null;
  recordUpdateComposerDefinition?: unknown | null;
}): DipNodeVariant {
  if (composer.entityComposerDefinition != null) return "entity";
  if (composer.relationComposerDefinition != null) return "relation";
  if (composer.recordUpdateComposerDefinition != null) return "record_update";
  return "relation";
}

function bcrJobPeek(
  jobId: string,
  initialTab: "info" | "changeRequests" = "changeRequests",
  recordComposerDefinitionId?: string,
): DipPeekTarget {
  return { type: "batchChangeRequestsJob", id: jobId, initialTab, recordComposerDefinitionId };
}

function composerPeek(composerId: string, variant: DipNodeVariant): DipPeekTarget {
  return { type: composerPeekType(variant), id: composerId };
}

function recordsPeek(jobId: string, composerId: string, variant: DipNodeVariant): DipPeekTarget {
  return {
    type: "records",
    id: composerId,
    batchChangeRequestsJobId: jobId,
    recordComposerDefinitionId: composerId,
    // record_update composers produce both entity and relation records; the peek shows both.
    recordKind: variant,
  };
}

function evidenceSetPeek(evidenceSetId: string): DipPeekTarget {
  return { type: "evidenceSet", id: evidenceSetId };
}

/** DIP detail → project info UI. ISO dates are formatted in the component. */
export function toProjectInfo(
  detail: Pick<DipDetail, "name" | "statusId" | "updatedAt" | "dataImportProjectStatusLogs" | "description"> & {
    dataImportProjectUsers: { user: DipDetail["dataImportProjectUsers"][number]["user"] | null }[];
  },
): DipProjectInfoUI {
  const logFor = (statusId: string) =>
    detail.dataImportProjectStatusLogs.find((l) => l.statusId === statusId)?.createdAt ?? null;
  return {
    title: detail.name,
    status: detail.statusId,
    statusTone: statusTone(detail.statusId),
    updatedAtISO: detail.updatedAt,
    startAtISO: logFor(PLANNING),
    endAtISO: logFor(DONE),
    users: detail.dataImportProjectUsers.map((u) => u.user?.displayName ?? "").filter((name) => name.length > 0),
    description: detail.description.length > 0 ? detail.description : null,
  };
}

interface ComposerAgg {
  id: string;
  name: string;
  isEntity: boolean;
  isDeactivated: boolean;
  detailPath: string;
  open: number;
  committed: number;
  rejected: number;
}

/** Sum per-composer aggregates across all BCR jobs of the DIP. */
/**
 * Per-composer CR counts, DIP-wide. The aggregate on `recordComposerDefinition`
 * is NOT job-scoped, so it is counted ONCE per composer (deduped by id) rather
 * than summed across the composer's job links (which would multiply it).
 */
function aggregateComposers(jobs: DipBatchChangeRequestsJobs, dataImportProjectId: string): ComposerAgg[] {
  const byId = new Map<string, ComposerAgg>();
  for (const job of jobs) {
    for (const link of job.batchChangeRequestsJobRecordComposerDefinitions) {
      const rcd = link.recordComposerDefinition;
      if (byId.has(rcd.id)) continue;
      byId.set(rcd.id, {
        id: rcd.id,
        name: rcd.name,
        isEntity: rcd.entityComposerDefinition != null,
        isDeactivated: rcd.deactivatedAt != null,
        detailPath: composerDetailPath(rcd.id, composerNodeVariant(rcd), dataImportProjectId),
        open: rcd.open.aggregate?.count ?? 0,
        committed: rcd.committed.aggregate?.count ?? 0,
        rejected: rcd.rejected.aggregate?.count ?? 0,
      });
    }
  }
  return [...byId.values()];
}

/** Sum job-level open/committed/rejected across all BCR jobs. */
function aggregateJobTotals(jobs: DipBatchChangeRequestsJobs) {
  return jobs.reduce(
    (acc, job) => ({
      open: acc.open + (job.open.aggregate?.count ?? 0),
      committed: acc.committed + (job.committed.aggregate?.count ?? 0),
      rejected: acc.rejected + (job.rejected.aggregate?.count ?? 0),
    }),
    { open: 0, committed: 0, rejected: 0 },
  );
}

/** Output summary groups. Labels resolve via i18n in the component. */
export function toOutputSummary(
  jobs: DipBatchChangeRequestsJobs,
  batchEvidencesJobs: DipBatchEvidencesJobs,
  evidenceCount: number,
  dataSourceStats: DipDataSourceStatsSummary,
  dataImportProjectId: string,
): DipSummaryGroup[] {
  const activeJobs = jobs.filter((job) => job.deactivatedAt == null);
  const activeBatchEvidencesJobs = batchEvidencesJobs.filter((job) => job.deactivatedAt == null);
  const totals = aggregateJobTotals(activeJobs);
  const composers = aggregateComposers(activeJobs, dataImportProjectId);
  const drafts = activeBatchEvidencesJobs.reduce(
    (count, job) => count + (job.unifiedEvidenceDraftsAggregate.aggregate?.count ?? 0),
    0,
  );
  const entities = composers.filter((c) => c.isEntity).reduce((n, c) => n + c.committed, 0);
  const relations = composers.filter((c) => !c.isEntity).reduce((n, c) => n + c.committed, 0);
  return [
    {
      titleKey: "summary.dataSources",
      metrics: [
        { labelKey: "summary.data", value: dataSourceStats.dataRowsCount, tone: "dataSource" },
        { labelKey: "summary.ready", value: dataSourceStats.readyRowsCount, tone: "dataSource" },
      ],
    },
    {
      titleKey: "summary.evidences",
      metrics: [
        { labelKey: "summary.drafts", value: drafts, tone: "evidenceDrafts" },
        { labelKey: "summary.latestEvidences", value: evidenceCount, tone: "evidenceSet" },
      ],
    },
    {
      titleKey: "summary.records",
      metrics: [
        { labelKey: "summary.changeRequestsOpen", value: totals.open, tone: "changeRequests" },
        { labelKey: "summary.changeRequestsApproved", value: totals.committed, tone: "changeRequests" },
        { labelKey: "summary.changeRequestsRejected", value: totals.rejected, tone: "changeRequests" },
        { labelKey: "summary.entities", value: entities, tone: "records" },
        { labelKey: "summary.relations", value: relations, tone: "records" },
      ],
    },
  ];
}

type DipComposers = GetDipRecordComposersQuery["recordComposerDefinitions"];
type DipComposerEvidenceSet = DipComposers[number]["recordComposerDefinitionEvidenceSets"][number]["evidenceSet"];
type DipBatchEvidenceSet = NonNullable<DipBatchEvidencesJobs[number]["evidenceSet"]>;

function hasPendingCorrections(
  evidenceSet: DipComposerEvidenceSet | DipBatchEvidenceSet,
): evidenceSet is DipComposerEvidenceSet {
  return "pendingCorrections" in evidenceSet;
}

function pendingCorrectionsCount(evidenceSet: DipComposerEvidenceSet | DipBatchEvidenceSet): number {
  return hasPendingCorrections(evidenceSet) ? (evidenceSet.pendingCorrections.aggregate?.count ?? 0) : 0;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function dataSourceIdFromComposerDefinition(definition: unknown): string | null {
  const evidenceFilter = asRecord(asRecord(definition)?.evidenceFilter);
  const metadata = asRecord(evidenceFilter?.metadata);
  const path = metadata?.path;
  const equals = metadata?.equals;
  return Array.isArray(path) && path.join(".") === "ops.identify.data_source_id" && typeof equals === "string"
    ? equals
    : null;
}

/**
 * Build the Compose section from DIP composers and their linked BCR jobs.
 * Older generated projects may not populate recordComposerDefinitionEvidenceSets, so we also infer
 * the source evidence set from the composer definition's data-source evidence filter.
 */
export function toComposeColumns(
  composers: DipComposers,
  beJobs: DipBatchEvidencesJobs,
  batchChangeRequestsJobs: DipBatchChangeRequestsJobs,
  dataImportProjectId: string,
  includeDeactivated: boolean,
): [DipPipelineColumn, DipPipelineColumn, DipPipelineColumn, DipPipelineColumn] {
  interface ComposerNode {
    id: string;
    name: string;
    deactivatedAt?: string | null;
    entityComposerDefinition?: { recordComposerDefinitionId: string } | null;
    relationComposerDefinition?: { recordComposerDefinitionId: string } | null;
    recordUpdateComposerDefinition?: { recordComposerDefinitionId: string } | null;
    definition?: unknown;
    open: { aggregate?: { count?: number | null } | null };
    committed: { aggregate?: { count?: number | null } | null };
    rejected: { aggregate?: { count?: number | null } | null };
  }

  interface BcrJobNode {
    id: string;
    name?: string | null;
    deactivatedAt?: string | null;
    open: { aggregate?: { count?: number | null } | null };
    committed: { aggregate?: { count?: number | null } | null };
    rejected: { aggregate?: { count?: number | null } | null };
    synthetic?: boolean;
  }

  interface ComposerAcc {
    composer: ComposerNode;
    jobIds: Set<string>;
  }

  interface BcrJobAcc {
    job: BcrJobNode;
    composers: Map<string, ComposerNode>;
  }

  interface EsAcc {
    id: string;
    name: string;
    insertionOrder: number;
    isDeactivated: boolean;
    count: number;
    uncommittedCount: number;
    // Corrected (newer-generation) evidences in this set not yet reflected in records.
    // It is an evidence-set-level metric, so it lives on the evidence-set node.
    pendingCorrections: number;
    toNodeIds: Set<string>;
  }

  const bcrJobsById = new Map<string, BcrJobAcc>();
  const composersById = new Map<string, ComposerAcc>();
  const esById = new Map<string, EsAcc>();
  const evidenceSetsByDataSourceId = new Map<string, NonNullable<DipBatchEvidencesJobs[number]["evidenceSet"]>>();
  const evidenceSetOrderById = new Map<string, number>();

  const addBcrJob = (job: BcrJobNode, composer?: ComposerNode) => {
    const bcrJobAcc = bcrJobsById.get(job.id) ?? {
      job,
      composers: new Map<string, ComposerNode>(),
    };
    if (composer) {
      bcrJobAcc.composers.set(composer.id, composer);
    }
    bcrJobsById.set(job.id, bcrJobAcc);

    if (!composer) return;

    const composerAcc = composersById.get(composer.id) ?? {
      composer,
      jobIds: new Set<string>(),
    };
    composerAcc.composer = composer;
    composerAcc.jobIds.add(job.id);
    composersById.set(composer.id, composerAcc);
  };

  const eligibleBcrJobs = batchChangeRequestsJobs.filter((job) => includeDeactivated || job.deactivatedAt == null);

  for (const beJob of beJobs) {
    if (!includeDeactivated && beJob.deactivatedAt != null) continue;
    const es = beJob.evidenceSet;
    if (es?.deactivatedAt != null && !includeDeactivated) continue;
    if (!es) continue;
    if (!evidenceSetOrderById.has(es.id)) {
      evidenceSetOrderById.set(es.id, evidenceSetOrderById.size);
    }
    evidenceSetsByDataSourceId.set(beJob.dataSource.id, es);
  }

  for (const job of eligibleBcrJobs) {
    addBcrJob(job);
    for (const link of job.batchChangeRequestsJobRecordComposerDefinitions) {
      if (!includeDeactivated && link.recordComposerDefinition.deactivatedAt != null) continue;
      addBcrJob(job, link.recordComposerDefinition);
    }
  }

  for (const composer of composers) {
    if (!includeDeactivated && composer.deactivatedAt != null) continue;
    const fallbackDataSourceId = dataSourceIdFromComposerDefinition(composer.definition);
    const fallbackEvidenceSet = evidenceSetsByDataSourceId.get(fallbackDataSourceId ?? "");
    const linkedEvidenceSets =
      composer.recordComposerDefinitionEvidenceSets.length > 0
        ? composer.recordComposerDefinitionEvidenceSets
            .map((link) => link.evidenceSet)
            .filter((evidenceSet) => includeDeactivated || evidenceSet.deactivatedAt == null)
        : fallbackEvidenceSet
          ? [fallbackEvidenceSet]
          : [];

    const linkedBcrJobs = composer.batchChangeRequestsJobRecordComposerDefinitions
      .map((bcrJobLink) => bcrJobLink.batchChangeRequestsJob)
      .filter(
        (bcrJob) =>
          bcrJob.dataImportProjectId === dataImportProjectId && (includeDeactivated || bcrJob.deactivatedAt == null),
      );

    const bcrJobsForComposer: BcrJobNode[] =
      linkedBcrJobs.length > 0
        ? linkedBcrJobs
        : eligibleBcrJobs.length === 1
          ? [eligibleBcrJobs[0]]
          : [
              {
                id: `unconfigured-${fallbackDataSourceId ?? composer.id}`,
                name: "BCRJob",
                open: { aggregate: { count: 0 } },
                committed: { aggregate: { count: 0 } },
                rejected: { aggregate: { count: 0 } },
                synthetic: true,
              },
            ];

    for (const bcrJob of bcrJobsForComposer) {
      addBcrJob(bcrJob, composer);
      for (const es of linkedEvidenceSets) {
        const esAcc = esById.get(es.id) ?? {
          id: es.id,
          name: es.name,
          insertionOrder: esById.size,
          isDeactivated: es.deactivatedAt != null,
          count: es.evidenceEvidenceSetsAggregate.aggregate?.count ?? 0,
          uncommittedCount: es.uncommittedEvidences.aggregate?.count ?? 0,
          // pendingCorrections only exists on the composer-linked evidenceSet (getDipRecordComposers);
          // the fallback evidence-set path comes from a different query without it, so it contributes 0.
          pendingCorrections: pendingCorrectionsCount(es),
          toNodeIds: new Set<string>(),
        };
        esAcc.toNodeIds.add(`bcr-comp-${bcrJob.id}-${composer.id}`);
        esById.set(es.id, esAcc);
      }
    }
  }

  const bcrJobItems: DipPipelineItem[] = [...bcrJobsById.values()].map(({ job, composers }) => {
    const composerChildren: DipPipelineItem[] = [...composers.values()].map((composer) => {
      const open = composer.open.aggregate?.count ?? 0;
      const nodeVariant = composerNodeVariant(composer);
      return makeComposerNode({
        id: `bcr-comp-${job.id}-${composer.id}`,
        detailPath: composerDetailPath(composer.id, nodeVariant, dataImportProjectId),
        peek: composerPeek(composer.id, nodeVariant),
        title: composer.name,
        openCount: open,
        isDeactivated: composer.deactivatedAt != null,
        to: `cr-${composer.id}`,
      });
    });

    return {
      ...makeBatchChangeRequestsJobNode({
        id: `bcr-${job.id}`,
        detailPath: job.synthetic ? null : batchChangeRequestsJobInfoPath(dataImportProjectId, job.id),
        peek: job.synthetic ? null : bcrJobPeek(job.id, "info"),
        title: job.name ?? "BCRJob",
        isDeactivated: job.deactivatedAt != null,
        to: composerChildren.length > 0 ? null : `cr-bcr-${job.id}`,
      }),
      renderAsGroup: true,
      children: composerChildren,
    };
  });

  const orderedEvidenceSets = [...esById.values()].sort((a, b) => {
    const aOrder = evidenceSetOrderById.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const bOrder = evidenceSetOrderById.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    return aOrder - bOrder || a.insertionOrder - b.insertionOrder;
  });

  const esItems: DipPipelineItem[] = orderedEvidenceSets.map((es) =>
    makeEvidenceSetNode({
      id: `ces-${es.id}`,
      detailPath: evidenceSetDetailPath(dataImportProjectId, es.id),
      peek: evidenceSetPeek(es.id),
      title: es.name,
      totalCount: es.count,
      pendingCount: es.uncommittedCount,
      pendingCorrections: es.pendingCorrections,
      isDeactivated: es.isDeactivated,
      alignToGroupChildIndex: 0,
      to: [...es.toNodeIds],
    }),
  );

  const crItems: DipPipelineItem[] = [...composersById.values()].map(({ composer, jobIds }) => {
    const bcrJobId = jobIds.size === 1 ? [...jobIds][0] : null;
    const bcrJob = bcrJobId ? bcrJobsById.get(bcrJobId)?.job : null;
    const open = composer.open.aggregate?.count ?? 0;
    const committed = composer.committed.aggregate?.count ?? 0;
    const rejected = composer.rejected.aggregate?.count ?? 0;
    const nodeVariant = composerNodeVariant(composer);
    return makeChangeRequestsNode({
      id: `cr-${composer.id}`,
      detailPath:
        bcrJobId && !bcrJob?.synthetic ? batchChangeRequestsJobChangeRequestsPath(dataImportProjectId, bcrJobId) : null,
      peek: bcrJobId && !bcrJob?.synthetic ? bcrJobPeek(bcrJobId, "changeRequests", composer.id) : null,
      title: "CRs",
      openCount: open,
      committedCount: committed,
      rejectedCount: rejected,
      nodeVariant,
      isDeactivated: composer.deactivatedAt != null,
      alignToGroupChildIndex: 0,
      to: `rec-${composer.id}`,
    });
  });

  const recordItems: DipPipelineItem[] = [...composersById.values()].map(({ composer, jobIds }) => {
    const bcrJobId = jobIds.size === 1 ? [...jobIds][0] : null;
    const bcrJob = bcrJobId ? bcrJobsById.get(bcrJobId)?.job : null;
    const committed = composer.committed.aggregate?.count ?? 0;
    const nodeVariant = composerNodeVariant(composer);
    return makeRecordsNode({
      id: `rec-${composer.id}`,
      detailPath: composerDetailPath(composer.id, nodeVariant, dataImportProjectId),
      peek: bcrJobId && !bcrJob?.synthetic ? recordsPeek(bcrJobId, composer.id, nodeVariant) : null,
      title: composerCategoryName(composer.definition) ?? composer.name,
      totalCount: committed,
      nodeVariant,
      isDeactivated: composer.deactivatedAt != null,
      alignToGroupChildIndex: 0,
      to: null,
    });
  });

  for (const { job, composers } of bcrJobsById.values()) {
    if (composers.size > 0) continue;
    const open = job.open.aggregate?.count ?? 0;
    const committed = job.committed.aggregate?.count ?? 0;
    const rejected = job.rejected.aggregate?.count ?? 0;
    crItems.push(
      makeChangeRequestsNode({
        id: `cr-bcr-${job.id}`,
        detailPath: job.synthetic ? null : batchChangeRequestsJobChangeRequestsPath(dataImportProjectId, job.id),
        peek: job.synthetic ? null : bcrJobPeek(job.id),
        title: "CRs",
        openCount: open,
        committedCount: committed,
        rejectedCount: rejected,
        isDeactivated: job.deactivatedAt != null,
        to: null,
      }),
    );
  }

  return [
    { titleKey: "evidenceSets", items: esItems },
    { titleKey: "bcrjob", items: bcrJobItems },
    { titleKey: "changeRequests", items: crItems, layout: "narrow" },
    { titleKey: "records", items: recordItems },
  ];
}

/**
 * Build the Import section's columns from
 * batchEvidencesJobs. Nodes are deduped by id; edges come from job rows (jobs without an importer
 * link DS→ES directly). DS/EI counts = evidence throughput; ES count = the evidence set's total.
 * DataSources linked to the DIP but not referenced by any BEJob are still shown as count-0 nodes
 * sourced from `projectDataSources`.
 */
export function toImportColumns(
  jobs: DipBatchEvidencesJobs,
  projectDataSources: DipProjectDataSources,
  dataImportProjectId: string,
  includeDeactivated: boolean,
): [DipPipelineColumn, DipPipelineColumn, DipPipelineColumn, DipPipelineColumn] {
  interface NodeAcc {
    id: string;
    name: string;
    isDeactivated: boolean;
    count: number;
    to: Set<string>;
    alignToGroupChildIndex: number;
  }
  const dsById = new Map<string, NodeAcc>();
  const beJobItems: DipPipelineItem[] = [];
  // One Drafts node per BEJob (drafts are scoped by batch_evidences_job_id).
  const draftsItems: DipPipelineItem[] = [];
  const esById = new Map<
    string,
    {
      id: string;
      name: string;
      isDeactivated: boolean;
      count: number;
      uncommittedCount: number;
      alignToGroupChildIndex: number;
    }
  >();

  for (const job of jobs) {
    if (!includeDeactivated && job.deactivatedAt != null) continue;
    const ds = job.dataSource;
    const ei = job.evidenceImporter;
    const es = job.evidenceSet;
    if (!es) continue;
    const includesDataSource = includeDeactivated || ds.deactivatedAt == null;
    const visibleEvidenceImporter = ei != null && (includeDeactivated || ei.deactivatedAt == null) ? ei : null;
    const includesEvidenceSet = includeDeactivated || es.deactivatedAt == null;
    const jobCount = job.evidencesAggregate.aggregate?.count ?? 0;

    if (includesDataSource) {
      const dsAcc = dsById.get(ds.id) ?? {
        id: ds.id,
        name: ds.name,
        isDeactivated: ds.deactivatedAt != null,
        count: 0,
        to: new Set<string>(),
        alignToGroupChildIndex: 0,
      };
      dsAcc.count += jobCount;
      if (visibleEvidenceImporter) dsAcc.alignToGroupChildIndex = 0;
      if (visibleEvidenceImporter) {
        dsAcc.to.add(`ei-${job.id}-${visibleEvidenceImporter.id}`);
      } else if (!ei && includesEvidenceSet) {
        dsAcc.to.add(`bej-${job.id}`);
      }
      dsById.set(ds.id, dsAcc);
    }

    beJobItems.push({
      ...makeBatchEvidencesJobNode({
        id: `bej-${job.id}`,
        detailPath: batchEvidencesJobDetailPath(dataImportProjectId, job.id),
        peek: { type: "batchEvidencesJob", id: job.id },
        title: job.name ?? "BEJob",
        isDeactivated: job.deactivatedAt != null,
        to: visibleEvidenceImporter ? null : `drafts-${job.id}`,
      }),
      renderAsGroup: true,
      children: visibleEvidenceImporter
        ? [
            makeEvidenceImporterNode({
              id: `ei-${job.id}-${visibleEvidenceImporter.id}`,
              detailPath: peekPathFor("evidenceImporter", visibleEvidenceImporter.id, dataImportProjectId),
              peek: { type: "evidenceImporter" as const, id: visibleEvidenceImporter.id },
              title: visibleEvidenceImporter.name,
              isDeactivated: visibleEvidenceImporter.deactivatedAt != null,
              to: `drafts-${job.id}`,
            }),
          ]
        : [],
    });

    // Drafts node (1:1 with the BEJob): badge = uncommitted draft count; peek = drafts list + Run/Dry-run.
    const draftCount = job.unifiedEvidenceDraftsAggregate.aggregate?.count ?? 0;
    draftsItems.push(
      makeEvidenceDraftsNode({
        id: `drafts-${job.id}`,
        peek: { type: "evidenceDrafts", id: job.id },
        title: "Drafts",
        totalCount: jobCount,
        pendingCount: draftCount,
        isDeactivated: job.deactivatedAt != null,
        alignToGroupChildIndex: 0,
        to: includesEvidenceSet ? [`es-${es.id}`] : null,
      }),
    );

    if (!includesEvidenceSet) continue;
    if (!esById.has(es.id)) {
      esById.set(es.id, {
        id: es.id,
        name: es.name,
        isDeactivated: es.deactivatedAt != null,
        count: es.evidenceEvidenceSetsAggregate.aggregate?.count ?? 0,
        uncommittedCount: es.uncommittedEvidences.aggregate?.count ?? 0,
        alignToGroupChildIndex: 0,
      });
    } else if (visibleEvidenceImporter) {
      const esAcc = esById.get(es.id);
      if (esAcc) esAcc.alignToGroupChildIndex = 0;
    }
  }

  for (const { dataSource } of projectDataSources) {
    if (dsById.has(dataSource.id)) continue;
    if (!includeDeactivated && dataSource.deactivatedAt != null) continue;
    dsById.set(dataSource.id, {
      id: dataSource.id,
      name: dataSource.name,
      isDeactivated: dataSource.deactivatedAt != null,
      count: 0,
      to: new Set<string>(),
      alignToGroupChildIndex: 0,
    });
  }

  const dsItems: DipPipelineItem[] = [...dsById.values()].map((d) =>
    makeDataSourceNode({
      id: `ds-${d.id}`,
      detailPath: dataSourceDetailPath(dataImportProjectId, d.id),
      peek: { type: "dataSource", id: d.id },
      title: d.name,
      totalCount: d.count,
      isDeactivated: d.isDeactivated,
      alignToGroupChildIndex: d.alignToGroupChildIndex,
      to: [...d.to],
    }),
  );

  const esItems: DipPipelineItem[] = [...esById.values()].map((s) =>
    makeEvidenceSetNode({
      id: `es-${s.id}`,
      detailPath: evidenceSetDetailPath(dataImportProjectId, s.id),
      peek: evidenceSetPeek(s.id),
      title: s.name,
      totalCount: s.count,
      pendingCount: s.uncommittedCount,
      isDeactivated: s.isDeactivated,
      alignToGroupChildIndex: s.alignToGroupChildIndex,
      to: null,
    }),
  );

  return [
    { titleKey: "dataSources", items: dsItems },
    { titleKey: "bejob", items: beJobItems },
    { titleKey: "drafts", items: draftsItems, layout: "narrow" },
    { titleKey: "evidenceSets", items: esItems },
  ];
}

/** Minimal shape we read from the jsonb `definition` (defensive — actual schema is richer). */
type CategoryNamesFilter = string[] | { in?: string[]; notIn?: string[] };
interface ResolverQuery {
  categoryNames?: CategoryNamesFilter;
  AND?: ResolverQuery[];
  OR?: ResolverQuery[];
}
interface ComposerDefShape {
  categoryNames?: string[];
  sourceResolver?: { query?: ResolverQuery };
  targetResolver?: { query?: ResolverQuery };
}

/** `categoryNames` may be a plain `string[]` or `{ in/notIn: string[] }` (backend allows both). */
function firstCategoryName(cn: CategoryNamesFilter | undefined): string | null {
  if (!cn) return null;
  if (Array.isArray(cn)) return cn[0] ?? null;
  return cn.in?.[0] ?? null;
}

/** DFS a resolver query for the first positive `categoryNames` (handles AND/OR nesting + both forms). */
function extractQueryCategory(query: ResolverQuery | undefined): string | null {
  if (!query) return null;
  const direct = firstCategoryName(query.categoryNames);
  if (direct) return direct;
  for (const child of query.AND ?? []) {
    const c = extractQueryCategory(child);
    if (c) return c;
  }
  for (const child of query.OR ?? []) {
    const c = extractQueryCategory(child);
    if (c) return c;
  }
  return null;
}

function composerCategoryName(definition: unknown): string | null {
  const def = (definition ?? {}) as unknown as ComposerDefShape;
  return def.categoryNames?.[0] ?? null;
}

/**
 * Derive the schema diagram (main entity → relation → target entity) from the DIP's composer
 * definitions. Reads the jsonb `definition` defensively; relation source/target come from the
 * resolver query `categoryNames`, matched to entity composers by category.
 */
export function toSchemaGraph(
  composers: GetDipRecordComposersQuery["recordComposerDefinitions"],
  dataImportProjectId: string,
): DipSchemaGraph {
  const entityByCategory = new Map<string, { id: string; name: string }>();
  let firstEntityCategory: string | null = null;
  for (const c of composers) {
    if (!c.entityComposerDefinition) continue;
    const cat = composerCategoryName(c.definition);
    if (cat && !entityByCategory.has(cat)) {
      entityByCategory.set(cat, { id: c.id, name: c.name });
      firstEntityCategory ??= cat;
    }
  }
  const entityFor = (cat: string) => entityByCategory.get(cat) ?? null;
  const titleFor = (cat: string) => entityFor(cat)?.name ?? cat;
  const entityDetailPathFor = (cat: string | null): string | null => {
    if (!cat) return null;
    const entity = entityFor(cat);
    return entity ? composerDetailPath(entity.id, "entity", dataImportProjectId) : null;
  };

  const relations: {
    id: string;
    title: string;
    category: string | null;
    sourceCat: string | null;
    targetCat: string | null;
  }[] = [];
  for (const c of composers) {
    if (!c.relationComposerDefinition) continue;
    const def = (c.definition ?? {}) as unknown as ComposerDefShape;
    relations.push({
      id: c.id,
      title: c.name,
      category: composerCategoryName(c.definition),
      sourceCat: extractQueryCategory(def.sourceResolver?.query),
      targetCat: extractQueryCategory(def.targetResolver?.query),
    });
  }

  if (relations.length === 0) return { mainTitle: null, mainCategory: null, mainDetailPath: null, pairs: [] };

  const sourceCounts = new Map<string, number>();
  for (const r of relations) {
    if (r.sourceCat) sourceCounts.set(r.sourceCat, (sourceCounts.get(r.sourceCat) ?? 0) + 1);
  }
  const mainCat = [...sourceCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? firstEntityCategory;

  const pairs: DipSchemaPair[] = relations.map((r) => ({
    relationId: r.id,
    relationTitle: r.title,
    relationCategory: r.category,
    relationDetailPath: composerDetailPath(r.id, "relation", dataImportProjectId),
    entityId: `ent-${r.id}`,
    entityTitle: r.targetCat ? titleFor(r.targetCat) : "—",
    entityCategory: r.targetCat,
    entityDetailPath: entityDetailPathFor(r.targetCat),
  }));

  return {
    mainTitle: mainCat ? titleFor(mainCat) : "—",
    mainCategory: mainCat,
    mainDetailPath: entityDetailPathFor(mainCat),
    pairs,
  };
}
